import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, XCircle, Shield, Edit, Eye, KeyRound, User } from "lucide-react";
import { useOrganization } from "@/contexts/OrganizationContext";
import { InviteUserDialog } from "@/components/InviteUserDialog";

interface User {
  id: string;
  email: string;
  approved: boolean;
  created_at: string;
  roles: string[];
}

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const { organization, isLoading: orgLoading } = useOrganization();

  const fetchUsers = async () => {
    if (!organization?.id) {
      setLoading(false);
      return;
    }

    try {
      // Fetch all profiles for the organization
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      const userIds = (profiles || []).map(p => p.id);

      // Batch fetch all roles in a single query (fixes N+1)
      const { data: allRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("user_id", userIds);

      if (rolesError) {
        console.error("Roles error:", rolesError);
      }

      // Group roles by user_id
      const rolesByUser = (allRoles || []).reduce((acc, r) => {
        if (!acc[r.user_id]) acc[r.user_id] = [];
        acc[r.user_id].push(r.role);
        return acc;
      }, {} as Record<string, string[]>);

      const usersWithRoles = (profiles || []).map(profile => ({
        ...profile,
        roles: rolesByUser[profile.id] || [],
      }));

      setUsers(usersWithRoles);
    } catch (error: any) {
      console.error("Fetch users error:", error);
      toast.error("Failed to fetch users: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when org context is loaded
    if (!orgLoading && organization?.id) {
      fetchUsers();
    }
  }, [organization?.id, orgLoading]);

  const approveUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ approved: true })
        .eq("id", userId);

      if (error) throw error;

      toast.success("User approved successfully");
      fetchUsers();
    } catch (error: any) {
      toast.error("Failed to approve user: " + error.message);
    }
  };

  const revokeAccess = async (userId: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ approved: false })
        .eq("id", userId);

      if (error) throw error;

      toast.success("User access revoked");
      fetchUsers();
    } catch (error: any) {
      toast.error("Failed to revoke access: " + error.message);
    }
  };

  const assignRole = async (userId: string, role: string) => {
    try {
      const { data: existingRole } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId)
        .eq("role", role)
        .maybeSingle();

      if (existingRole) {
        toast.info(`User already has ${role} role`);
        return;
      }

      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role });

      if (error) throw error;

      toast.success(`${role} role assigned`);
      fetchUsers();
    } catch (error: any) {
      toast.error(`Failed to assign ${role} role: ` + error.message);
    }
  };

  const removeRole = async (userId: string, role: string) => {
    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role);

      if (error) throw error;

      toast.success(`${role} role removed`);
      fetchUsers();
    } catch (error: any) {
      toast.error(`Failed to remove ${role} role: ` + error.message);
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success(`Password reset email sent to ${email}`);
    } catch (error: any) {
      toast.error(`Failed to send password reset: ` + error.message);
    }
  };

  if (loading) {
    return <div className="p-8">Loading users...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">User Management</h1>
        {organization && (
          <InviteUserDialog 
            organizationId={organization.id}
            organizationName={organization.name}
            onSuccess={fetchUsers}
          />
        )}
      </div>
      
      <div className="space-y-4">
        {users.map((user) => (
          <Card key={user.id} className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <p className="font-medium">{user.email}</p>
                  
                  {user.roles.includes("admin") && (
                    <Badge variant="secondary" className="gap-1">
                      <Shield className="h-3 w-3" />
                      Admin
                    </Badge>
                  )}
                  {user.roles.includes("editor") && (
                    <Badge variant="secondary" className="gap-1">
                      <Edit className="h-3 w-3" />
                      Editor
                    </Badge>
                  )}
                  {user.roles.includes("moderator") && (
                    <Badge variant="secondary" className="gap-1">
                      <Eye className="h-3 w-3" />
                      Moderator
                    </Badge>
                  )}
                  {user.roles.includes("user") && (
                    <Badge variant="outline" className="gap-1">
                      <User className="h-3 w-3" />
                      User
                    </Badge>
                  )}
                  
                  <Badge variant={user.approved ? "default" : "destructive"}>
                    {user.approved ? "Approved" : "Pending"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Joined: {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {!user.approved && (
                  <Button
                    onClick={() => approveUser(user.id)}
                    size="sm"
                    variant="default"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                )}
                
                {user.approved && (
                  <>
                    <Button
                      onClick={() => sendPasswordReset(user.email)}
                      size="sm"
                      variant="outline"
                    >
                      <KeyRound className="h-4 w-4 mr-1" />
                      Reset Password
                    </Button>
                    
                    <Button
                      onClick={() => revokeAccess(user.id)}
                      size="sm"
                      variant="destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Revoke
                    </Button>
                    
                    {!user.roles.includes("admin") && (
                      <Button
                        onClick={() => assignRole(user.id, "admin")}
                        size="sm"
                        variant="secondary"
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Admin
                      </Button>
                    )}
                    {user.roles.includes("admin") && (
                      <Button
                        onClick={() => removeRole(user.id, "admin")}
                        size="sm"
                        variant="outline"
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Remove Admin
                      </Button>
                    )}
                    
                    {!user.roles.includes("editor") && (
                      <Button
                        onClick={() => assignRole(user.id, "editor")}
                        size="sm"
                        variant="secondary"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Editor
                      </Button>
                    )}
                    {user.roles.includes("editor") && (
                      <Button
                        onClick={() => removeRole(user.id, "editor")}
                        size="sm"
                        variant="outline"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Remove Editor
                      </Button>
                    )}
                    
                    {!user.roles.includes("moderator") && (
                      <Button
                        onClick={() => assignRole(user.id, "moderator")}
                        size="sm"
                        variant="secondary"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Moderator
                      </Button>
                    )}
                    {user.roles.includes("moderator") && (
                      <Button
                        onClick={() => removeRole(user.id, "moderator")}
                        size="sm"
                        variant="outline"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Remove Moderator
                      </Button>
                    )}
                    
                    {!user.roles.includes("user") && (
                      <Button
                        onClick={() => assignRole(user.id, "user")}
                        size="sm"
                        variant="secondary"
                      >
                        <User className="h-4 w-4 mr-1" />
                        User
                      </Button>
                    )}
                    {user.roles.includes("user") && (
                      <Button
                        onClick={() => removeRole(user.id, "user")}
                        size="sm"
                        variant="outline"
                      >
                        <User className="h-4 w-4 mr-1" />
                        Remove User
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
        
        {users.length === 0 && (
          <Card className="p-6 text-center text-muted-foreground">
            No users found
          </Card>
        )}
      </div>
    </div>
  );
};

export default Users;
